// The phone tab bar's overflow (DM1, phase 8): everything that did not fit in
// four tabs, plus the account details and sign-out that live in the sidebar
// footer on desktop. Reached only from the bottom tab bar in practice, but it
// is an ordinary route — the back button works, and a desktop visitor who
// types /app/more just sees a menu.
import { Link, useOutletContext } from 'react-router-dom';
import { useSession } from '../session';
import { useGuardedSignOut } from '../offline/hooks';
import { Badge, type ShellOutletContext } from '../components/AppShell';
import {
  GUIDES_URL,
  NOTIFICATIONS_NAV,
  moreDestinations,
  ROLE_LABEL,
  type Role,
} from '../components/navConfig';

export function MorePage() {
  const { user, tenant } = useSession();
  // Says so first when forms on this phone have not been sent (round 42).
  const signOut = useGuardedSignOut();
  // The shell's own Notifications badge (see ShellOutletContext): one poll,
  // one number, so this page's feed row and the sidebar cannot disagree.
  // Defensive because a page must render wherever it is mounted, and outside
  // the shell there is no context to read.
  const shell = useOutletContext<ShellOutletContext | null>();
  const feedBadge = shell?.feedBadge ?? { count: 0, urgent: false };
  if (!user || !tenant) return null;

  const role = user.role as Role;
  // The platform flag puts the owner console on the list — the phone's only
  // door to it, since the desktop sidebar is the only other place it is drawn.
  const destinations = moreDestinations(role, user.isPlatformAdmin, {
    timesheets: tenant?.timesheetsEnabled ?? false,
  });

  return (
    <div className="mx-auto max-w-md">
      <h1 className="mb-4 text-xl font-semibold text-gray-900">More</h1>

      {destinations.length > 0 && (
        <nav
          className="mb-6 divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white"
          aria-label="More destinations"
        >
          {destinations.map(({ to, label, icon }) => (
            <Link
              key={to}
              to={to}
              className="flex min-h-[3rem] items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600"
            >
              <span className="text-gray-400">{icon}</span>
              <span className="flex-1">{label}</span>
              {to === NOTIFICATIONS_NAV.to && (
                <Badge count={feedBadge.count} urgent={feedBadge.urgent} />
              )}
              <svg
                className="h-4 w-4 flex-none text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          ))}
        </nav>
      )}

      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
        <p className="truncate text-sm font-medium text-gray-900" title={user.name}>
          {user.name}
        </p>
        <p className="truncate text-xs text-gray-500">
          {ROLE_LABEL[role]} · {tenant.name}
        </p>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-3 flex min-h-[2.75rem] w-full items-center justify-center rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
        >
          Sign out
        </button>
        {/* The documents the account was opened under, findable after signup —
            signup's footnote was the only place that linked them until the
            terms-acceptance work (migration 016) made them worth finding. */}
        <p className="mt-3 text-center text-xs text-gray-500">
          {/* The same address the sidebar's Help & guides uses (round 38) —
              one constant, so the phone and the desktop cannot drift. */}
          <a
            href={GUIDES_URL}
            target="_blank"
            rel="noreferrer"
            className="text-gray-500 underline hover:text-gray-700"
          >
            Help &amp; guides
          </a>
          {' · '}
          <a
            href="https://dayboard.com.au/terms.html"
            target="_blank"
            rel="noreferrer"
            className="text-gray-500 underline hover:text-gray-700"
          >
            Terms
          </a>
          {' · '}
          <a
            href="https://dayboard.com.au/privacy.html"
            target="_blank"
            rel="noreferrer"
            className="text-gray-500 underline hover:text-gray-700"
          >
            Privacy
          </a>
        </p>
      </div>
    </div>
  );
}
